import { Dispatch } from 'redux';
import { AxiosResponse } from 'axios';
import { DispatchFunction, IRole, IRoles, IRoleMemberCount, IActiveFilter, ICustomSelect } from '@app/models';
import {
    GET_PEOPLE_COUNT_IN_ROLE, GET_ROLE, GET_ROLES, GET_ROLES_MEMBER_COUNT, ROLES_COUNT, GET_MORE_ROLES, GET_MORE_ROLES_MEMBER_COUNT, GET_USERS_TO_ROLES,
    GET_GROUPS_COUNT_IN_ROLE, GET_GROUPS_TO_ROLES, ROLES_FOR_SELECT
} from '@app/store/constants';
import { rolesService } from '@app/services';
import { loadingData } from './basic.actions';
import { store } from '@app/helpers';
import { getModalSwitch, getLoaderBtn, getConfirmModalSwitch } from '.';
import { toast } from 'react-toastify';

export function handleGetRolesData(
    orderBy: string,
    asc: string,
    top: number,
    skip: number,
    organizationId: string,
    isReset: boolean,
    filters?: IActiveFilter[]
): DispatchFunction {
    return (dispatch: Dispatch) => {
        if (isReset) {
            dispatch(loadingData(true));
            dispatch(rolesCount(0));
            dispatch(getRoles({} as IRoles));
        }
        rolesService
            .getRoles(orderBy, asc, top, skip, organizationId, filters)
            .then((response: AxiosResponse) => {
                const roles = response.data.value;
                const roleIds = getMembersCountSetup(roles);

                const prevCount: number = store.getState().roles.count;
                const count: number = response.data['@odata.count'];
                if (prevCount !== count) { dispatch(rolesCount(count)); }

                !isReset ? dispatch(getMoreRoles({ roles })) : dispatch(getRoles({ roles }));

                if (roles.length) {
                    rolesService.getRoleMemberCount(roleIds)
                        .then((membersCount => {
                            !isReset ? dispatch(getMoreMembersCount(membersCount.data.value)) : dispatch(getMembersCount(membersCount.data.value));
                        }))
                        .then(() => {
                            if (isReset) { dispatch(loadingData(false)); }
                        })
                        .catch(() => {
                            // TODO: Uncomment when we will set up dev variable
                            // console.log(error);
                            if (isReset) { dispatch(loadingData(false)); }
                        });
                }
                else if (isReset) {
                    dispatch(loadingData(false));
                }
            });
    };

    function getMembersCountSetup(roles: IRole[]) {
        let roleIds: string[] = [];
        roles.forEach((element: IRole) => {
            roleIds.push(element.Id);
        });

        return roleIds;
    }
}

export function handleRolesForSelect(
    orderBy: string,
    asc: string,
    top: number,
    skip: number,
    organizationId: string,
    filters?: IActiveFilter[]): DispatchFunction {
    return ((dispatch: Dispatch) => {
        rolesService.getRoles(orderBy, asc, top, skip, organizationId, filters)
            .then((response: AxiosResponse) => {
                const groups: IRole[] = response.data.value;
                const selectRoles: ICustomSelect[] = [];
                groups.forEach((group: IRole) => {
                    selectRoles.push({
                        id: group.Id,
                        name: group.Name
                    });
                });
                dispatch(rolesForSelect(selectRoles));
            });
    });
}

export function handleAddUsersToRole(
    roleId: string,
    People: any[]
): DispatchFunction {
    return ((dispatch: Dispatch) => {
        rolesService.addUserToRole(roleId, People)
            .then((response: AxiosResponse) => {
                dispatch(addUsersToRole(response.data));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .then(() => {
                rolesService.getPeopleCountInRole(roleId)
                    .then((response: AxiosResponse) => {
                        dispatch(getPeopleCountInRole(response.data.value));
                    })
                    .then(() => dispatch(loadingData(false)));
            })
            .catch((error) => toast.error(error));
    });
}

export function handleRemoveGroupsFromRole(
    roleId: string,
    roleMembership: any[]
): DispatchFunction {
    return ((dispatch: Dispatch) => {
        rolesService.removeGroupsFromRole(roleId, roleMembership)
            .then((response: AxiosResponse) => {
                dispatch(addGroupsToRole(response.data));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .then(() => {
                rolesService.getGroupCountInRole(roleId)
                    .then((response: AxiosResponse) => {
                        dispatch(getGroupCountInRole(response.data.value));
                    })
                    .then(() => dispatch(loadingData(false)));
            })
            .catch((error) => toast.error(error));
    });
}

export function handleAddGroupsToRole(
    roleId: string,
    Groups: any[]
): DispatchFunction {
    return ((dispatch: Dispatch) => {
        rolesService.addGroupToRole(roleId, Groups)
            .then((response: AxiosResponse) => {
                dispatch(addGroupsToRole(response.data));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .then(() => {
                rolesService.getGroupCountInRole(roleId)
                    .then((response: AxiosResponse) => {
                        dispatch(getGroupCountInRole(response.data.value));
                    })
                    .then(() => dispatch(loadingData(false)));
            })
            .catch((error) => toast.error(error));
    });
}

export function handleRemoveUsersFromRole(
    roleId: string,
    roleMembership: any[]
): DispatchFunction {
    return ((dispatch: Dispatch) => {
        rolesService.removeUsersFromRole(roleId, roleMembership)
            .then((response: AxiosResponse) => {
                dispatch(addUsersToRole(response.data));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .then(() => {
                rolesService.getPeopleCountInRole(roleId)
                    .then((response: AxiosResponse) => {
                        dispatch(getPeopleCountInRole(response.data.value));
                    })
                    .then(() => dispatch(loadingData(false)));
            })
            .catch((error) => toast.error(error));
    });
}

export function handleGetPeopleCountInRole(roleId: string): DispatchFunction {
    return ((dispatch: Dispatch) => {
        rolesService.getPeopleCountInRole(roleId)
            .then((response: AxiosResponse) => {
                dispatch(getPeopleCountInRole(response.data.value));
            });
    });
}

export function handleGetGroupsCountInRole(roleId: string): DispatchFunction {
    return ((dispatch: Dispatch) => {
        rolesService.getGroupCountInRole(roleId)
            .then((response: AxiosResponse) => {
                dispatch(getGroupCountInRole(response.data.value));
            });
    });
}

export function handleGetRoleData(roleId: string): DispatchFunction {
    return ((dispatch: Dispatch) => {
        dispatch(loadingData(true));
        rolesService.getRole(roleId)
            .then((response: AxiosResponse) => {
                dispatch(getRole(response.data));
            })
            .then(() => dispatch(loadingData(false)));
    });
}

export function getRole(groupData: IRole) {
    return {
        payload: groupData,
        type: GET_ROLE
    };
}


export function getRoles(roles: IRoles) {
    return {
        payload: roles,
        type: GET_ROLES
    };
}

export function getMoreRoles(roles: IRoles) {
    return {
        payload: roles,
        type: GET_MORE_ROLES
    };
}

export function getMembersCount(roleMemberCount: IRoleMemberCount[]) {
    return {
        payload: roleMemberCount,
        type: GET_ROLES_MEMBER_COUNT
    };
}

export function getMoreMembersCount(roleMemberCount: IRoleMemberCount[]) {
    return {
        payload: roleMemberCount,
        type: GET_MORE_ROLES_MEMBER_COUNT
    };
}

export function rolesCount(count: number) {
    return {
        payload: count,
        type: ROLES_COUNT
    };
}

export function getPeopleCountInRole(peopleCount: number) {
    return {
        payload: peopleCount,
        type: GET_PEOPLE_COUNT_IN_ROLE
    };
}

export function addUsersToRole(usersToRole: any[]) {
    return {
        payload: usersToRole,
        type: GET_USERS_TO_ROLES
    };
}

export function getGroupCountInRole(groupCount: number) {
    return {
        payload: groupCount,
        type: GET_GROUPS_COUNT_IN_ROLE
    };
}

export function addGroupsToRole(groupsToRole: any[]) {
    return {
        payload: groupsToRole,
        type: GET_GROUPS_TO_ROLES
    };
}

export function rolesForSelect(roles: ICustomSelect[]) {
    return {
        payload: roles,
        type: ROLES_FOR_SELECT
    };
}
